import { sessionStatusEnums } from 'utils';

const STATUS = sessionStatusEnums;

export const SEGMENT_TYPE = { ACTIVE: 'ACTIVE', PAUSED: 'PAUSED' };
export const DEFAULT_PAUSE_SECONDS = 60;
export const MIN_ACTIVE_SPAN_MS = 3000;
export const TICK_INTERVAL_MS = 1000;

const segId = (suffix) => `seg-${Date.now()}-${suffix}`;
const ms = (d) => new Date(d).getTime();
const clampMs = (val, min, max) => Math.min(Math.max(val, min), max);

/* ---------- events ↔ segments ---------- */

export const eventsToSegments = (events = []) => {
    const segments = [];
    for (let i = 0; i < events.length; i++) {
        const curr = events[i];
        if (curr.status === STATUS.COMPLETED) break;
        const next = events[i + 1];
        segments.push({
            id: `seg-${i}`,
            type: curr.status === STATUS.IN_PROGRESS ? SEGMENT_TYPE.ACTIVE : SEGMENT_TYPE.PAUSED,
            start: new Date(curr.datetime),
            end: next ? new Date(next.datetime) : null,
        });
    }
    return segments;
};

export const segmentsToEvents = (segments, sessionStatus) => {
    const events = segments.map((seg) => ({
        datetime: new Date(seg.start).toISOString(),
        status: seg.type === SEGMENT_TYPE.ACTIVE ? STATUS.IN_PROGRESS : STATUS.PAUSED,
    }));
    const last = segments[segments.length - 1];
    if (sessionStatus === STATUS.COMPLETED && last?.end) {
        events.push({ datetime: new Date(last.end).toISOString(), status: STATUS.COMPLETED });
    }
    return events;
};

/* ---------- durations ---------- */

export const segmentDurationMs = (seg, now) => {
    const endMs = seg.end ? ms(seg.end) : now;
    return Math.max(0, endMs - ms(seg.start));
};

const sumByType = (segments, type, now) =>
    segments.filter((s) => s.type === type).reduce((acc, s) => acc + segmentDurationMs(s, now), 0);

export const totalActiveMs = (segments, now) => sumByType(segments, SEGMENT_TYPE.ACTIVE, now);
export const totalPausedMs = (segments, now) => sumByType(segments, SEGMENT_TYPE.PAUSED, now);

export const isRunningSegment = (seg) => !seg?.end;
export const hasRunningSegment = (segments) =>
    segments.length > 0 && isRunningSegment(segments[segments.length - 1]);

/* ---------- mutations (pure: returns new array) ---------- */

export const updateBoundary = (segments, boundaryIndex, date) => {
    if (!date) return segments;

    const now = Date.now();
    const minMs = boundaryIndex > 0 ? ms(segments[boundaryIndex - 1].start) : -Infinity;
    const right = segments[boundaryIndex];
    const maxMs = right?.end ? Math.min(now, ms(right.end)) : now;
    const clamped = new Date(clampMs(ms(date), minMs, maxMs));

    const next = [...segments];
    if (boundaryIndex === 0) {
        next[0] = { ...next[0], start: clamped };
    } else if (boundaryIndex === next.length) {
        next[next.length - 1] = { ...next[next.length - 1], end: clamped };
    } else {
        next[boundaryIndex - 1] = { ...next[boundaryIndex - 1], end: clamped };
        next[boundaryIndex] = { ...next[boundaryIndex], start: clamped };
    }
    return next;
};

/**
 * Returns the new segments array with a pause inserted in the middle of the
 * targeted active period, or `null` if the period is too short.
 */
export const tryAddPause = (segments, activeIndex) => {
    const seg = segments[activeIndex];
    if (!seg || seg.type !== SEGMENT_TYPE.ACTIVE) return null;

    const startMs = ms(seg.start);
    const running = isRunningSegment(seg);
    const endMs = running ? Date.now() : ms(seg.end);
    const span = endMs - startMs;
    if (span < MIN_ACTIVE_SPAN_MS) return null;

    const pauseMs = Math.max(1000, Math.min(DEFAULT_PAUSE_SECONDS * 1000, Math.floor(span / 3)));
    const pauseStart = new Date(startMs + Math.floor((span - pauseMs) / 2));
    const pauseEnd = new Date(pauseStart.getTime() + pauseMs);

    const next = [...segments];
    next.splice(
        activeIndex,
        1,
        { id: segId('a1'), type: SEGMENT_TYPE.ACTIVE, start: seg.start, end: pauseStart },
        { id: segId('p'), type: SEGMENT_TYPE.PAUSED, start: pauseStart, end: pauseEnd },
        { id: segId('a2'), type: SEGMENT_TYPE.ACTIVE, start: pauseEnd, end: running ? null : seg.end }
    );
    return next;
};

/**
 * Removes a pause segment and merges the adjacent active periods into one.
 * Returns `null` if the index does not point at a pause flanked by two actives.
 */
export const removePause = (segments, pausedIndex) => {
    const before = segments[pausedIndex - 1];
    const target = segments[pausedIndex];
    const after = segments[pausedIndex + 1];
    if (!target || target.type !== SEGMENT_TYPE.PAUSED || !before || !after) return null;

    const next = [...segments];
    next.splice(pausedIndex - 1, 3, {
        id: segId('merged'),
        type: SEGMENT_TYPE.ACTIVE,
        start: before.start,
        end: after.end,
    });
    return next;
};

/* ---------- date-picker bounds ---------- */

const cap = (date) => {
    const now = new Date();
    return date && date < now ? date : now;
};

export const startPickerBounds = (segments, idx) => ({
    min: idx === 0 ? null : new Date(segments[idx - 1].start),
    max: cap(segments[idx]?.end ? new Date(segments[idx].end) : null),
});

export const endPickerBounds = (segments, idx) => ({
    min: new Date(segments[idx].start),
    max: cap(segments[idx + 1]?.end ? new Date(segments[idx + 1].end) : null),
});

/* ---------- validation ---------- */

export const validateSegments = (segments, sessionStatus) => {
    if (!segments.length) return 'Timeline must contain at least one segment';
    if (segments[0].type !== SEGMENT_TYPE.ACTIVE) return 'First segment must be Active';

    const now = Date.now();
    let prevEnd = null;

    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const isLast = i === segments.length - 1;
        const startMs = ms(seg.start);

        if (Number.isNaN(startMs)) return `Segment ${i + 1}: invalid start time`;
        if (startMs > now) return `Segment ${i + 1}: start cannot be in the future`;
        if (prevEnd !== null && startMs !== prevEnd) {
            return `Segment ${i + 1}: must start when previous ends`;
        }

        if (seg.end !== null) {
            const endMs = ms(seg.end);
            if (Number.isNaN(endMs)) return `Segment ${i + 1}: invalid end time`;
            if (endMs > now) return `Segment ${i + 1}: end cannot be in the future`;
            if (endMs <= startMs) return `Segment ${i + 1}: end must be after start`;
            prevEnd = endMs;
            continue;
        }

        // open-ended segment is only valid as the trailing segment of a running OR paused session,
        // and its type must match the session state (IN_PROGRESS→ACTIVE, PAUSED→PAUSED).
        const expectedTrailingType =
            sessionStatus === STATUS.IN_PROGRESS
                ? SEGMENT_TYPE.ACTIVE
                : sessionStatus === STATUS.PAUSED
                ? SEGMENT_TYPE.PAUSED
                : null;
        if (!isLast || expectedTrailingType === null || seg.type !== expectedTrailingType) {
            return `Segment ${i + 1}: missing end time`;
        }
    }
    return null;
};
